// src/features/wf3/popup/W3FichaModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import s from "./W3FichaModal.module.css";
import { salvarW3, reprogramarW3ToW2, finalizarW3 } from "../api";

import W3ReprogramarModal from "./w3.reprogramar.jsx";
import W3EditarModal from "./W3EditarModal.jsx";
import W3Porcentagem from "./W3Porcentagem";
import W3FinalizarModal from "./W3FinalizarModal.jsx";
import W3JornadaIrregularModal from "./W3JornadaIrregularModal.jsx";
import {
  validarCamposObrigatoriosFinalizar,
  W3FinalizarValidatorModal,
} from "./W3FinalizarValidator.jsx";

/* ===== Helpers genéricos ===== */
const normStr = (v) => (v == null ? "" : String(v));
const safeTrim = (v) => normStr(v).trim();
const hasText = (v) => safeTrim(v).length > 0;

/* ===== Helpers tempo ===== */
const canonTimeHHMMSS = (raw) => {
  const m = normStr(raw)
    .trim()
    .match(/^(\d{1,3}):([0-5]\d)(?::([0-5]\d))?$/);
  if (!m) return "";
  const hh = String(Number(m[1])).padStart(2, "0");
  const mm = m[2];
  const ss = m[3] || "00";
  return `${hh}:${mm}:${ss}`;
};

const toHHMMInput = (raw) => canonTimeHHMMSS(raw).slice(0, 5) || "";

const toMinutes = (any) => {
  const m = normStr(any)
    .trim()
    .match(/^(\d{1,3}):([0-5]\d)(?::([0-5]\d))?$/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};

const sumIntervalsMinutes = (obj) => {
  const P = [
    ["primeiro_inicio", "primeiro_fim"],
    ["segundo_inicio", "segundo_fim"],
    ["terceiro_inicio", "terceiro_fim"],
  ];
  let t = 0;
  for (const [a, b] of P) {
    const A = toMinutes(obj?.[a]);
    const B = toMinutes(obj?.[b]);
    if (A != null && B != null && B > A) t += B - A;
  }
  return t;
};

const toHHMM = (mins) => {
  if (!mins || mins <= 0) return "00:00";
  const h = String(Math.floor(mins / 60)).padStart(2, "0");
  const m = String(mins % 60).padStart(2, "0");
  return `${h}:${m}`;
};

const toHHMMSSFromMinutes = (mins) => `${toHHMM(mins)}:00`;

/* ===== Números pt-BR ===== */
const parsePtBRNumber = (text) => {
  if (text == null || text === "") return null;
  const normalized = normStr(text)
    .replace(/\s+/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const formatPtBRNumber = (val) => {
  if (val == null || val === "") return "";
  const num = typeof val === "number" ? val : parsePtBRNumber(val);
  if (num == null) return "";
  return new Intl.NumberFormat("pt-BR").format(num);
};

const clamp0 = (n) => Math.max(0, Number(n || 0));

const toNumberOrNull = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/* ===== Correções leves (prévias) ===== */
const CORRECOES = [
  [/\brpévia\b/gi, "prévia"],
  [/\bprevia\b/gi, "prévia"],
  [/\bprim[ée]ria\b/gi, "primeira"],
  [/\bsegubda\b/gi, "segunda"],
  [/\binicio\b/gi, "início"],
  [/\btermino\b/gi, "término"],
  [/\bobs\b/gi, "observação"],
  [/\bencerr\b/gi, "encerramento"],
];

const corrigePT = (txt = "") => {
  let out = normStr(txt || "");
  for (const [re, rep] of CORRECOES) out = out.replace(re, rep);
  return out.replace(/\s{2,}/g, " ").trim();
};

/* Exibição RO: tira .000 */
const stripTrailingZeros = (v) => {
  const s = normStr(v).trim();
  if (!s) return "";
  const norm = s.replace(",", ".");
  const parts = norm.split(".");
  if (parts.length === 2 && /^0+$/.test(parts[1])) return parts[0];
  return s;
};

/* ===== Campo de hora estável ===== */
function StableTimeField({
  name,
  label,
  value,
  onCommit,
  extraClassName,
  labelNowrap = false,
  disabled = false,
}) {
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const commit = (v) => {
    if (disabled) return;
    onCommit?.(name, toHHMMInput(v));
  };

  return (
    <label className={s.field}>
      <span
        className={s.label}
        style={labelNowrap ? { whiteSpace: "nowrap" } : undefined}
      >
        {label}
      </span>
      <input
        className={`${s.input} ${extraClassName || ""}`}
        type="time"
        step={60}
        name={name}
        value={draft}
        onChange={(e) => {
          const v = e.target.value;
          setDraft(v);
          commit(v);
        }}
        onBlur={(e) => commit(e.target.value)}
        disabled={disabled}
        readOnly={disabled}
      />
    </label>
  );
}

export default function W3FichaModal({
  open = false,
  data = {},
  onClose = () => {},
  onSaved = () => {},
}) {
  if (!open) return null;

  const tempoDoBanco = canonTimeHHMMSS(
    data?.tempo_total || data?.total_tempo || ""
  );

  const initialQtdNumRaw =
    data.quantidade_real != null && data.quantidade_real !== ""
      ? data.quantidade_real
      : "";

  const initialQtdNum = toNumberOrNull(initialQtdNumRaw);

  /* ===== Estado ===== */
  const [dados, setDados] = useState({
    // RO (W2)
    codigo_unico: data.codigo_unico || data.id || "",
    atividade: data.atividade || "",
    ativo: data.ativo || "",
    prioridade: data.prioridade || data.po || "",
    gerencia: data.gerencia || "",
    coordenador: data.coordenador || "",
    sub: data.sub || data.Sub || data.SUB || "",
    kmInicio: data.kmInicio || "",
    kmFinal: data.kmFinal || "",
    qtd: data.qtd || data.quantidade || "",
    horario_inicio_turma: toHHMMInput(
      data.horario_inicio_jornada_turma || data.horario_inicio_turma
    ),
    horario_do_intervalo: toHHMMInput(data.horario_do_intervalo),
    tempo_do_intervalo: toHHMMInput(data.tempo_do_intervalo),
    sbOrigem: data.sbOrigem || "",
    sbDeTrabalho: data.sbDeTrabalho || "",
    sbEncerramento: data.sbEncerramento || "",
    quantidade_maquinista: data.quantidade_maquinista || "",
    maquinista: data.maquinista || "",
    tipo_acionamento: data.tipo_acionamento || "",
    tipo_programacao: data.tipo_programacao || "",
    locomotiva1: data.locomotiva1 || "",
    locomotiva2: data.locomotiva2 || "",
    observacao: data.observacao || "",

    // Editáveis W3
    primeira_previa: data.primeira_previa ?? "",
    previa_intervalo: toHHMMInput(
      data.previa_intervalo ??
        data.previsao_inicio_intervalo ??
        data.previsao_intervalo ??
        data.previsao_inicio_do_intervalo ??
        data.previsao_inicio ??
        data.previsao_de_inicio_do_intervalo ??
        ""
    ),
    previsao_encerramento_jornada: toHHMMInput(
      data.previsao_encerramento_jornada ??
        data.previsao_fim_jornada ??
        data.previsao_fim ??
        ""
    ),
    segunda_previa: data.segunda_previa ?? "",
    primeiro_inicio: toHHMMInput(data.primeiro_inicio),
    primeiro_fim: toHHMMInput(data.primeiro_fim),
    segundo_inicio: toHHMMInput(data.segundo_inicio),
    segundo_fim: toHHMMInput(data.segundo_fim),
    terceiro_inicio: toHHMMInput(data.terceiro_inicio),
    terceiro_fim: toHHMMInput(data.terceiro_fim),
    tempo_total: tempoDoBanco || "",
    quantidade_real: initialQtdNum ?? "",
    unidade_medida:
      data.unidade_de_medida || data.unidade_medida || data.medida || "",
    status_wf3: data.status_wf3 || data.status_workflow || "EM_ANDAMENTO",
    responsavel: data.responsavel || "",
    impacto_fechamento: data.impacto_fechamento || "",
    causa_fechamento: data.causa_fechamento || "",
    justificativa_fechamento: data.justificativa_fechamento || "",
    data: data.data || "",

    aceite_jornada_irregular: data.aceite_jornada_irregular || "",
    quantidade_horas_irregulares: data.quantidade_horas_irregulares
      ? canonTimeHHMMSS(data.quantidade_horas_irregulares)
      : "",
  });

  const [qtdText, setQtdText] = useState(
    initialQtdNum != null ? formatPtBRNumber(initialQtdNum) : ""
  );

  const [salvando, setSalvando] = useState(false);
  const [editarPopupAberto, setEditarPopupAberto] = useState(false);
  const [reprogramarPopupAberto, setReprogramarPopupAberto] = useState(false);
  const [finalizarPopupAberto, setFinalizarPopupAberto] = useState(false);
  const [jornadaModalAberto, setJornadaModalAberto] = useState(false);
  const [validatorModalAberto, setValidatorModalAberto] = useState(false);
  const [faltandoCamposFinalizar, setFaltandoCamposFinalizar] = useState([]);
  const firstLoad = useRef(true);

  /* sincronizar campo de texto da quantidade com quantidade_real */
  useEffect(() => {
    const n = toNumberOrNull(dados.quantidade_real);
    setQtdText(n != null ? formatPtBRNumber(n) : "");
  }, [dados.quantidade_real]);

  /* ===== Tempo total exec ===== */
  const tempoTotalMinutos = useMemo(
    () =>
      sumIntervalsMinutes({
        primeiro_inicio: dados.primeiro_inicio,
        primeiro_fim: dados.primeiro_fim,
        segundo_inicio: dados.segundo_inicio,
        segundo_fim: dados.segundo_fim,
        terceiro_inicio: dados.terceiro_inicio,
        terceiro_fim: dados.terceiro_fim,
      }),
    [
      dados.primeiro_inicio,
      dados.primeiro_fim,
      dados.segundo_inicio,
      dados.segundo_fim,
      dados.terceiro_inicio,
      dados.terceiro_fim,
    ]
  );

  useEffect(() => {
    const algum = [
      dados.primeiro_inicio,
      dados.primeiro_fim,
      dados.segundo_inicio,
      dados.segundo_fim,
      dados.terceiro_inicio,
      dados.terceiro_fim,
    ].some((v) => normStr(v).length >= 4);

    if (algum) {
      setDados((prev) => ({
        ...prev,
        tempo_total: toHHMMSSFromMinutes(tempoTotalMinutos),
      }));
    } else if (firstLoad.current) {
      firstLoad.current = false;
      setDados((prev) => ({
        ...prev,
        tempo_total: tempoDoBanco || prev.tempo_total || "",
      }));
    }
  }, [
    tempoTotalMinutos,
    dados.primeiro_inicio,
    dados.primeiro_fim,
    dados.segundo_inicio,
    dados.segundo_fim,
    dados.terceiro_inicio,
    dados.terceiro_fim,
    tempoDoBanco,
  ]);

  const tempoTotalParaMostrar = useMemo(() => {
    const algum = [
      dados.primeiro_inicio,
      dados.primeiro_fim,
      dados.segundo_inicio,
      dados.segundo_fim,
      dados.terceiro_inicio,
      dados.terceiro_fim,
    ].some((v) => normStr(v).length >= 4);

    if (algum && tempoTotalMinutos > 0) return toHHMM(tempoTotalMinutos);

    return (
      (canonTimeHHMMSS(dados.tempo_total) ||
        canonTimeHHMMSS(tempoDoBanco) ||
        "00:00"
      ).slice(0, 5)
    );
  }, [
    tempoTotalMinutos,
    dados.tempo_total,
    tempoDoBanco,
    dados.primeiro_inicio,
    dados.primeiro_fim,
    dados.segundo_inicio,
    dados.segundo_fim,
    dados.terceiro_inicio,
    dados.terceiro_fim,
  ]);

  /* ===== Cálculos para aderência ===== */
  const tempoIntervaloPrevistoMinutos = useMemo(
    () => toMinutes(dados.tempo_do_intervalo),
    [dados.tempo_do_intervalo]
  );

  const quantidadePrevistaNum = useMemo(
    () => toNumberOrNull(dados.qtd),
    [dados.qtd]
  );

  const quantidadeRealizadaNum = useMemo(
    () => toNumberOrNull(dados.quantidade_real),
    [dados.quantidade_real]
  );

  /* ===== Jornada total (início -> previsão de encerramento) ===== */
  const {
    cargaHorariaUltrapassada,
    excessoMinutos,
    jornadaTotalMinutos,
  } = useMemo(() => {
    const inicioMin = toMinutes(dados.horario_inicio_turma);
    const fimMin = toMinutes(dados.previsao_encerramento_jornada);

    if (inicioMin == null || fimMin == null) {
      return {
        cargaHorariaUltrapassada: false,
        excessoMinutos: 0,
        jornadaTotalMinutos: null,
      };
    }

    const totalMin = fimMin - inicioMin;

    if (totalMin <= 0) {
      return {
        cargaHorariaUltrapassada: false,
        excessoMinutos: 0,
        jornadaTotalMinutos: totalMin,
      };
    }

    const excesso = totalMin - 9 * 60;

    return {
      cargaHorariaUltrapassada: excesso > 0,
      excessoMinutos: excesso > 0 ? excesso : 0,
      jornadaTotalMinutos: totalMin,
    };
  }, [dados.horario_inicio_turma, dados.previsao_encerramento_jornada]);

  const excessoHHMM = excessoMinutos > 0 ? toHHMM(excessoMinutos) : "";
  const jornadaTotalHHMM =
    jornadaTotalMinutos != null ? toHHMM(jornadaTotalMinutos) : "";

  // Finalizado = qualquer status_wf3 (ou status_workflow) == "FINALIZADO"
  const estaFinalizado =
    safeTrim(
      dados.status_wf3 || data.status_wf3 || data.status_workflow
    ).toLowerCase() === "finalizado";

  // Detectar se o W3 foi reprogramado (apenas histórico)
  const isReprogramado = (() => {
    const sw = safeTrim(
      data.status_workflow || data.status_wf3 || ""
    ).toLowerCase();
    const ativo = safeTrim(data.ativo || "").toLowerCase();

    return (
      sw === "reprogramar" ||
      sw === "reprogramado" ||
      ativo === "nao_executar" ||
      ativo === "não_executar" ||
      ativo === "nao executar" ||
      ativo === "não executar"
    );
  })();

  // Bloqueia tudo se finalizado OU reprogramado
  const bloqueado = estaFinalizado || isReprogramado;
  const faltamPrevisoes =
    !hasText(dados.previa_intervalo) || !hasText(dados.previsao_encerramento_jornada);
  const bloqueadoExecucao = bloqueado || faltamPrevisoes;

  /* ===== Handlers ===== */
  const handleQtdChange = (e) => {
    if (bloqueadoExecucao) return;
    let text = e.target.value.replace(/[^\d.,]/g, "");

    if (text.trim() === "") {
      setQtdText("");
      setDados((p) => ({ ...p, quantidade_real: "" }));
      return;
    }

    const parsed = parsePtBRNumber(text);
    const num = parsed == null ? null : clamp0(parsed);

    setQtdText(text);
    setDados((p) => ({ ...p, quantidade_real: num ?? "" }));
  };

  const handleQtdBlur = () => {
    if (bloqueadoExecucao) return;
    if (!hasText(qtdText)) {
      setQtdText("");
      setDados((p) => ({ ...p, quantidade_real: "" }));
      return;
    }

    const parsed = parsePtBRNumber(qtdText);
    if (parsed == null) {
      setQtdText("");
      setDados((p) => ({ ...p, quantidade_real: "" }));
      return;
    }

    const num = clamp0(parsed);
    setQtdText(formatPtBRNumber(num));
    setDados((p) => ({ ...p, quantidade_real: num }));
  };

  const handleTextoBlurCorrigir = (e) => {
    if (bloqueadoExecucao) return;
    const { name, value } = e.target;
    if (!["primeira_previa", "segunda_previa"].includes(name)) return;
    const fix = corrigePT(value);
    if (fix !== value) setDados((p) => ({ ...p, [name]: fix }));
  };

  const commitTime = (key, hhmm) => {
    if (bloqueado) return;
    // campos de previsão liberam o restante, então permitem mesmo bloqueadoExecucao
    if (bloqueadoExecucao && key !== "previa_intervalo" && key !== "previsao_encerramento_jornada")
      return;

    setDados((prev) => {
      const next = { ...prev, [key]: hhmm };

      // Sempre que mexer em início ou previsão de encerramento da jornada:
      // - zera aceite
      // - zera horas irregulares
      // - se passar de 9h, abre modal de novo
      if (
        key === "horario_inicio_turma" ||
        key === "previsao_encerramento_jornada"
      ) {
        const inicioMin = toMinutes(next.horario_inicio_turma);
        const fimMin = toMinutes(next.previsao_encerramento_jornada);

        next.aceite_jornada_irregular = "";
        next.quantidade_horas_irregulares = "";

        if (inicioMin != null && fimMin != null) {
          const totalMin = fimMin - inicioMin;
          const excesso = totalMin - 9 * 60;

          if (totalMin > 0 && excesso > 0) {
            setJornadaModalAberto(true);
          }
        }
      }

      return next;
    });
  };

  // ===== Guard para abrir o FINALIZAR =====
  const handleAbrirFinalizar = () => {
    if (bloqueado) return;

    const faltando = validarCamposObrigatoriosFinalizar(
      dados,
      quantidadeRealizadaNum
    );

    if (faltando.length > 0) {
      setFaltandoCamposFinalizar(faltando);
      setValidatorModalAberto(true);
      return;
    }

    setFinalizarPopupAberto(true);
  };

  /* ===== Persistência ===== */
  const sanitizePayload = (raw) => {
    const allowed = new Set([
      "codigo_unico",
      "primeira_previa",
      "previa_intervalo",
      "previsao_encerramento_jornada",
      "segunda_previa",
      "primeiro_inicio",
      "primeiro_fim",
      "segundo_inicio",
      "segundo_fim",
      "terceiro_inicio",
      "terceiro_fim",
      "tempo_total",
      "quantidade_real",
      "unidade_medida",
      // "status_wf3",
      "responsavel",
      "impacto_fechamento",
      "causa_fechamento",
      "justificativa_fechamento",
      "motivo_alteracao",
      "etapa_nova",
      "data",
      "sub",
      "aceite_jornada_irregular",
      "quantidade_horas_irregulares",
    ]);

    const clean = {};
    for (const [k, v] of Object.entries(raw)) {
      if (!allowed.has(k)) continue;
      if (v === undefined) continue;
      clean[k] = v;
    }

    if (!hasText(clean.unidade_medida)) {
      delete clean.unidade_medida;
    }

    // Se vier vazio, limpa no banco (NULL)
    if ("aceite_jornada_irregular" in clean) {
      if (!hasText(clean.aceite_jornada_irregular)) {
        clean.aceite_jornada_irregular = null;
      }
    }

    if ("quantidade_horas_irregulares" in clean) {
      if (!hasText(clean.quantidade_horas_irregulares)) {
        clean.quantidade_horas_irregulares = null;
      }
    }

    return clean;
  };

  const salvar = async () => {
    if (salvando || bloqueadoExecucao) return false;

    // Se passou de 9h e não tem aceite, obriga a passar pelo modal
    if (cargaHorariaUltrapassada && !hasText(dados.aceite_jornada_irregular)) {
      setJornadaModalAberto(true);
      return false;
    }

    setSalvando(true);
    try {
      const corr = {
        ...dados,
        primeira_previa: corrigePT(dados.primeira_previa || ""),
        segunda_previa: corrigePT(dados.segunda_previa || ""),
      };

      if (!cargaHorariaUltrapassada) {
        // Não passa mais de 9h → limpa aceite e horas irregulares
        corr.aceite_jornada_irregular = "";
        corr.quantidade_horas_irregulares = "";
      } else {
        // Passa de 9h e (teoricamente) já tem aceite → garante HH:MM:SS coerente
        corr.quantidade_horas_irregulares =
          excessoMinutos > 0 ? toHHMMSSFromMinutes(excessoMinutos) : "";
      }

      const algumExec = [
        corr.primeiro_inicio,
        corr.primeiro_fim,
        corr.segundo_inicio,
        corr.segundo_fim,
        corr.terceiro_inicio,
        corr.terceiro_fim,
      ].some((v) => normStr(v).length >= 4);

      const tempoSalvar = algumExec
        ? toHHMMSSFromMinutes(
            sumIntervalsMinutes({
              primeiro_inicio: corr.primeiro_inicio,
              primeiro_fim: corr.primeiro_fim,
              segundo_inicio: corr.segundo_inicio,
              segundo_fim: corr.segundo_fim,
              terceiro_inicio: corr.terceiro_inicio,
              terceiro_fim: corr.terceiro_fim,
            })
          )
        : canonTimeHHMMSS(corr.tempo_total) ||
          canonTimeHHMMSS(tempoDoBanco) ||
          null;

      const payload = sanitizePayload({
        ...corr,
        quantidade_real:
          quantidadeRealizadaNum != null && !Number.isNaN(quantidadeRealizadaNum)
            ? quantidadeRealizadaNum
            : 0,
        tempo_total: tempoSalvar,
        codigo_unico: dados.codigo_unico,
        motivo_alteracao: "Atualização WF-3",
      });

      await salvarW3(payload);
      onSaved();
      return true;
    } catch (e) {
      alert(
        "Erro ao salvar: " +
          (e?.response?.data?.error || e.message || "Erro desconhecido")
      );
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const salvarReprogramacao = async (form) => {
    if (salvando) return;
    const {
      responsabilidade,
      impacto,
      causa,
      justificativa,
      marcar_w3,
      novaData,
      novoHorario,
      prioridade,
      respAceite,
      w2_patch = {},
    } = form || {};

    setSalvando(true);
    try {
      const codigo = safeTrim(dados.codigo_unico || data.codigo_unico || "");
      if (!codigo) throw new Error("Sem código único para reprogramar.");

      await reprogramarW3ToW2({
        codigo_unico: codigo,
        responsabilidade,
        impacto,
        causa,
        justificativa: justificativa || "Reprogramação solicitada",
        marcar_w3: marcar_w3 !== undefined ? !!marcar_w3 : true,
        w2_patch: {
          ...w2_patch,
          ...(novaData ? { data: novaData } : {}),
          ...(novoHorario ? { horario_do_intervalo: novoHorario } : {}),
          ...(prioridade ? { prioridade } : {}),
          ...(respAceite ? { responsavel_pelo_aceite: respAceite } : {}),
        },
      });

      setReprogramarPopupAberto(false);
      onSaved();
    } catch (e) {
      alert("Erro ao reprogramar: " + (e?.message || "Erro desconhecido"));
    } finally {
      setSalvando(false);
    }
  };

  /* ===== UI helpers ===== */
  const ReadOnly = ({ label, value, formatter }) => (
    <label className={s.field}>
      <span className={s.label}>{label}</span>
      <input
        className={`${s.input} ${s.inputRO}`}
        value={(formatter ? formatter(value) : value ?? "") || "—"}
        readOnly
        disabled
      />
    </label>
  );

  // REPROGRAMAÇÃO (direto da wf_3)
  const respReprog = safeTrim(data.causador ?? "");
  const impactoReprog = safeTrim(data.causa_primaria ?? "");
  const causaReprog = safeTrim(data.causa_secundaria ?? "");
  const justReprog = safeTrim(data.justificativa_fechamento ?? "");

  // FECHAMENTO (FINALIZADO) – 3 campos do banco + justificativa
  const responsabilidadeFechamento = safeTrim(data.causador ?? "");
  const impactoFechamento = safeTrim(
    data.impacto_fechamento ?? data.causa_primaria ?? ""
  );
  const causaFechamento = safeTrim(
    data.causa_fechamento ?? data.causa_secundaria ?? ""
  );
  const justificativaFechamento = safeTrim(data.justificativa_fechamento ?? "");

  // Último usuário que movimentou – principal em usuario_responsavel
  const ultimoUsuarioMovimentou = safeTrim(
    data.usuario_responsavel ??
      data.ultimo_usuario_movimentou ??
      data.ultimo_usuario ??
      data.usuario_ultima_movimentacao ??
      data.atualizado_por ??
      data.updated_by ??
      ""
  );

  return (
    <div className={s.backdrop}>
      <div
        className={`${s.modal} ${s.root}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={s.header}>
          <div className={s.title}>Ficha da Execução</div>
          <button className={s.close} aria-label="Fechar" onClick={onClose}>
            ×
          </button>
        </div>

        <div className={s.content}>
          {/* ===== BLOCO RO (W2) ===== */}
          <div className={s.blockRO}>
            <div className={`${s.grid} ${s.grid3}`}>
              <ReadOnly label="Atividade" value={dados.atividade} />
              <ReadOnly label="Ativo" value={dados.ativo} />
              <ReadOnly
                label="Quantidade"
                value={dados.qtd}
                formatter={stripTrailingZeros}
              />

              <ReadOnly label="Gerência" value={dados.gerencia} />
              <ReadOnly label="Coordenador" value={dados.coordenador} />
              <ReadOnly label="Prioridade" value={dados.prioridade} />

              <ReadOnly label="Sub" value={dados.sub} />
              <ReadOnly
                label="KM de Início"
                value={dados.kmInicio}
                formatter={stripTrailingZeros}
              />
              <ReadOnly
                label="KM Final"
                value={dados.kmFinal}
                formatter={stripTrailingZeros}
              />

              <ReadOnly
                label="Horário de Início da Jornada da Turma"
                value={toHHMMInput(dados.horario_inicio_turma)}
              />
              <ReadOnly
                label="Horário do Intervalo"
                value={toHHMMInput(dados.horario_do_intervalo)}
              />
              <ReadOnly
                label="Tempo do Intervalo"
                value={toHHMMInput(dados.tempo_do_intervalo)}
              />

              <ReadOnly label="Pátio de Origem" value={dados.sbOrigem} />
              <ReadOnly label="SB de Trabalho" value={dados.sbDeTrabalho} />
              <ReadOnly
                label="Pátio de Encerramento"
                value={dados.sbEncerramento}
              />

              <ReadOnly
                label="Quantidade de Maquinista"
                value={dados.quantidade_maquinista}
              />
              <ReadOnly label="Maquinista" value={dados.maquinista} />
              <ReadOnly
                label="Tipo de Acionamento"
                value={dados.tipo_acionamento}
              />

              <ReadOnly
                label="Tipo de Programação"
                value={dados.tipo_programacao}
              />
              <ReadOnly label="Locomotiva 1" value={dados.locomotiva1} />
              <ReadOnly label="Locomotiva 2" value={dados.locomotiva2} />
            </div>

            <label className={`${s.field} ${s.full}`}>
              <span className={s.label}>Observação</span>
              <textarea
                className={`${s.textarea} ${s.inputRO}`}
                value={dados.observacao || ""}
                readOnly
                disabled
              />
            </label>
          </div>

          {/* ===== BLOCO EDIT (W3 – Execução) ===== */}
          {!isReprogramado && (
            <div className={s.blockEdit}>
              <div className={s.sectionTitleRow}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    gap: 16,
                  }}
                >
                  <div className={s.sectionTitle}>Execução</div>
                  <div style={{ alignSelf: "flex-start" }}>
                    <W3Porcentagem
                      tempoPrevistoMinutos={tempoIntervaloPrevistoMinutos}
                      tempoRealizadoMinutos={tempoTotalMinutos}
                      quantidadePrevista={quantidadePrevistaNum}
                      quantidadeRealizada={quantidadeRealizadaNum}
                    />
                  </div>
                </div>
              </div>

              {/* Linha com previsões e excedente — 3 colunas iguais */}
              <div
                className={`${s.grid} ${s.grid3}`}
                style={{ marginBottom: 8 }}
              >
                <StableTimeField
                  name="previa_intervalo"
                  label="Previsão de Início do Intervalo"
                  labelNowrap
                  value={dados.previa_intervalo || ""}
                  onCommit={commitTime}
                  extraClassName={s.timeInput}
                  disabled={bloqueado}
                />

                <StableTimeField
                  name="previsao_encerramento_jornada"
                  label="Previsão de Encerramento da Jornada"
                  labelNowrap
                  value={dados.previsao_encerramento_jornada || ""}
                  onCommit={commitTime}
                  extraClassName={s.timeInput}
                  disabled={bloqueado}
                />

                <label className={s.field}>
                  <span className={s.label}>Excedente</span>
                  <input
                    className={s.input}
                    type="time"
                    step={60}
                    value={excessoHHMM || ""}
                    readOnly
                    disabled
                  />
                </label>
              </div>

              {/* Primeira Prévia – full width */}
              <label className={`${s.field} ${s.full}`}>
                <span className={s.label}>Primeira Prévia</span>
              <textarea
                  className={s.textarea}
                  name="primeira_previa"
                  value={dados.primeira_previa || ""}
                  onChange={(e) =>
                    setDados((p) => ({
                      ...p,
                      primeira_previa: e.target.value,
                    }))
                  }
                  onBlur={handleTextoBlurCorrigir}
                  disabled={bloqueadoExecucao}
                  readOnly={bloqueadoExecucao}
                />
              </label>

              {/* Inícios / Términos */}
              <div
                className={`${s.grid} ${s.grid2}`}
                style={{ marginTop: 12 }}
              >
                <StableTimeField
                  name="primeiro_inicio"
                  label="Início 1"
                  value={dados.primeiro_inicio || ""}
                  onCommit={commitTime}
                  disabled={bloqueadoExecucao}
                />
                <StableTimeField
                  name="primeiro_fim"
                  label="Término 1"
                  value={dados.primeiro_fim || ""}
                  onCommit={commitTime}
                  disabled={bloqueadoExecucao}
                />
                <StableTimeField
                  name="segundo_inicio"
                  label="Início 2"
                  value={dados.segundo_inicio || ""}
                  onCommit={commitTime}
                  disabled={bloqueadoExecucao}
                />
                <StableTimeField
                  name="segundo_fim"
                  label="Término 2"
                  value={dados.segundo_fim || ""}
                  onCommit={commitTime}
                  disabled={bloqueadoExecucao}
                />
                <StableTimeField
                  name="terceiro_inicio"
                  label="Início 3"
                  value={dados.terceiro_inicio || ""}
                  onCommit={commitTime}
                  disabled={bloqueadoExecucao}
                />
                <StableTimeField
                  name="terceiro_fim"
                  label="Término 3"
                  value={dados.terceiro_fim || ""}
                  onCommit={commitTime}
                  disabled={bloqueadoExecucao}
                />
              </div>

              {/* Totais e Qtd */}
              <div
                className={`${s.grid} ${s.grid3}`}
                style={{ marginTop: 12 }}
              >
                <label className={s.field}>
                  <span className={s.label}>Total de Horas Realizadas</span>
                  <input
                    className={s.input}
                    value={tempoTotalParaMostrar}
                    readOnly
                    disabled
                  />
                </label>

                <label className={s.field}>
                  <span className={s.label}>Quantidade Realizada</span>
                  <input
                    className={s.input}
                    type="text"
                    name="quantidade_real"
                    inputMode="numeric"
                  pattern="[0-9\.,]*"
                  value={qtdText}
                  onChange={handleQtdChange}
                  onBlur={handleQtdBlur}
                  disabled={bloqueadoExecucao}
                  readOnly={bloqueadoExecucao}
                />
              </label>

                <label className={s.field}>
                  <span className={s.label}>Unidade de Medida</span>
                  <input
                    className={s.input}
                    value={dados.unidade_medida || ""}
                    readOnly
                    disabled
                  />
                </label>
              </div>

              {/* Segunda Prévia */}
              <label className={`${s.field} ${s.full}`}>
                <span className={s.label}>Segunda Prévia</span>
                <textarea
                  className={s.textarea}
                  name="segunda_previa"
                  value={dados.segunda_previa || ""}
                  onChange={(e) =>
                    setDados((p) => ({ ...p, segunda_previa: e.target.value }))
                  }
                  onBlur={handleTextoBlurCorrigir}
                  disabled={bloqueadoExecucao}
                  readOnly={bloqueadoExecucao}
                />
              </label>
            </div>
          )}

          {/* Quando REPROGRAMADO: mostra os 3 motivos + justificativa */}
          {isReprogramado && (
            <div className={s.blockEdit}>
              <div className={s.sectionTitleRow}>
                <div className={s.sectionTitle}>Reprogramação</div>
              </div>
              <p className={s.readonlyHint}>
                Esta atividade foi reprogramada. A ficha WF-3 permanece apenas
                para consulta.
              </p>

              <div className={`${s.grid} ${s.grid3}`} style={{ marginTop: 12 }}>
                <ReadOnly
                  label="Responsabilidade"
                  value={respReprog || "—"}
                />
                <ReadOnly label="Impacto" value={impactoReprog || "—"} />
                <ReadOnly label="Causa" value={causaReprog || "—"} />
              </div>

              <label
                className={`${s.field} ${s.full}`}
                style={{ marginTop: 12 }}
              >
                <span className={s.label}>Justificativa</span>
                <textarea
                  className={`${s.textarea} ${s.inputRO}`}
                  value={justReprog || ""}
                  readOnly
                  disabled
                />
              </label>
            </div>
          )}

          {/* Quando FINALIZADO: mostra responsabilidade, impacto, causa + justificativa + último usuário */}
          {estaFinalizado && (
            <div className={s.blockEdit}>
              <div className={s.sectionTitleRow}>
                <div className={s.sectionTitle}>Fechamento</div>
              </div>

              <p className={s.readonlyHint}>
                Esta atividade foi finalizada. Os dados abaixo registram o
                motivo do fechamento.
              </p>

              <div
                className={`${s.grid} ${s.grid3}`}
                style={{ marginTop: 12 }}
              >
                <ReadOnly
                  label="Responsabilidade do fechamento"
                  value={responsabilidadeFechamento || "—"}
                />
                <ReadOnly
                  label="Impacto do fechamento"
                  value={impactoFechamento || "—"}
                />
                <ReadOnly
                  label="Causa do fechamento"
                  value={causaFechamento || "—"}
                />
              </div>

              <label
                className={`${s.field} ${s.full}`}
                style={{ marginTop: 12 }}
              >
                <span className={s.label}>Justificativa do fechamento</span>
                <textarea
                  className={`${s.textarea} ${s.inputRO}`}
                  value={justificativaFechamento || ""}
                  readOnly
                  disabled
                />
              </label>

              <p className={s.readonlyHint} style={{ marginTop: 8 }}>
                Último usuário que movimentou:{" "}
                <strong>{ultimoUsuarioMovimentou || "—"}</strong>
              </p>
            </div>
          )}
        </div>

        {/* Botões: somem se finalizado OU reprogramado */}
        {!bloqueado && (
          <div className={s.actions}>
            <span />
            <div className={s.actionsRight}>
              <button
                className={s.btn}
                onClick={() => setEditarPopupAberto(true)}
                disabled={salvando}
              >
                Editar
              </button>
              <button
                className={s.btn}
                onClick={() => setReprogramarPopupAberto(true)}
                disabled={salvando}
              >
                Reprogramar
              </button>
              <button
                className={s.btn}
                onClick={() => salvar()}
                disabled={salvando}
              >
                {salvando ? "Salvando..." : "Salvar"}
              </button>
              <button
                className={`${s.btn} ${s.btnPrimary}`}
                onClick={handleAbrirFinalizar}
                disabled={salvando}
              >
                Finalizar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Popups auxiliares */}
      {editarPopupAberto && (
        <W3EditarModal
          open={editarPopupAberto}
          data={dados}
          onClose={() => setEditarPopupAberto(false)}
          onSaved={(atualizado) => {
            setDados((prev) => ({
              ...prev,
              ...atualizado,
              unidade_medida:
                atualizado.unidade_de_medida ||
                atualizado.unidade_medida ||
                atualizado.medida ||
                prev.unidade_medida ||
                prev.medida ||
                "",
            }));
            setEditarPopupAberto(false);
            onSaved();
          }}
        />
      )}

      {reprogramarPopupAberto && (
        <W3ReprogramarModal
          initial={dados}
          onClose={() => setReprogramarPopupAberto(false)}
          onConfirm={(form) =>
            salvarReprogramacao({
              ...form,
              marcar_w3: form.marcar_w3,
              novaData: form.novaData,
              novoHorario: form.novoHorario,
              prioridade: form.prioridade,
              respAceite: form.respAceite,
              w2_patch: form.w2_patch || {},
            })
          }
        />
      )}

      {finalizarPopupAberto && (
        <W3FinalizarModal
          open={finalizarPopupAberto}
          data={dados}
          onClose={() => setFinalizarPopupAberto(false)}
          onConfirm={async (formFechamento) => {
            if (salvando) return;

            // Mesma trava de jornada do SALVAR:
            if (
              cargaHorariaUltrapassada &&
              !hasText(dados.aceite_jornada_irregular)
            ) {
              setJornadaModalAberto(true);
              return;
            }

            setSalvando(true);
            try {
              // Corrige textos das prévias
              const corr = {
                ...dados,
                primeira_previa: corrigePT(dados.primeira_previa || ""),
                segunda_previa: corrigePT(dados.segunda_previa || ""),
                quantidade_real: quantidadeRealizadaNum ?? dados.quantidade_real,
              };

              // Jornada irregular: mesma lógica do SALVAR
              if (!cargaHorariaUltrapassada) {
                corr.aceite_jornada_irregular = "";
                corr.quantidade_horas_irregulares = "";
              } else {
                corr.quantidade_horas_irregulares =
                  excessoMinutos > 0
                    ? toHHMMSSFromMinutes(excessoMinutos)
                    : "";
              }

              // Tempo total para salvar
              const algumExec = [
                corr.primeiro_inicio,
                corr.primeiro_fim,
                corr.segundo_inicio,
                corr.segundo_fim,
                corr.terceiro_inicio,
                corr.terceiro_fim,
              ].some((v) => normStr(v).length >= 4);

              const tempoSalvar = algumExec
                ? toHHMMSSFromMinutes(
                    sumIntervalsMinutes({
                      primeiro_inicio: corr.primeiro_inicio,
                      primeiro_fim: corr.primeiro_fim,
                      segundo_inicio: corr.segundo_inicio,
                      segundo_fim: corr.segundo_fim,
                      terceiro_inicio: corr.terceiro_inicio,
                      terceiro_fim: corr.terceiro_fim,
                    })
                  )
                : canonTimeHHMMSS(corr.tempo_total) ||
                  canonTimeHHMMSS(tempoDoBanco) ||
                  null;

              // Apenas EXECUÇÃO passa no sanitizePayload
              const payloadExec = sanitizePayload({
                ...corr,
                tempo_total: tempoSalvar,
                codigo_unico: dados.codigo_unico,
                motivo_alteracao: "Finalização WF-3",
              });

              // Juntamos EXEC + FECHAMENTO + identificação (id/codigo_unico)
              const payloadFinal = {
                id: data.id,
                codigo_unico: dados.codigo_unico,
                ...payloadExec, // execução
                ...formFechamento, // responsabilidade_fechamento, impacto_fechamento, etc.
              };

              // (Opcional) log local
              // console.log("[W3FichaModal] payload FINALIZAR =", payloadFinal);

              await finalizarW3(payloadFinal);
              setFinalizarPopupAberto(false);
              onSaved();
            } catch (e) {
              alert(
                "Erro ao finalizar: " +
                  (e?.response?.data?.error ||
                    e.message ||
                    "Erro desconhecido")
              );
            } finally {
              setSalvando(false);
            }
          }}
        />
      )}

      {validatorModalAberto && (
        <W3FinalizarValidatorModal
          open={validatorModalAberto}
          faltando={faltandoCamposFinalizar}
          onClose={() => setValidatorModalAberto(false)}
        />
      )}

      {jornadaModalAberto && (
        <W3JornadaIrregularModal
          open={jornadaModalAberto}
          onClose={() => setJornadaModalAberto(false)}
          jornadaTotalHHMM={jornadaTotalHHMM}
          excessoHHMM={excessoHHMM}
          inicioJornada={toHHMMInput(dados.horario_inicio_turma)}
          previsaoEncerramento={dados.previsao_encerramento_jornada || ""}
          previsaoIntervalo={
            dados.previa_intervalo ||
            toHHMMInput(dados.horario_do_intervalo) ||
            ""
          }
          tempoIntervalo={toHHMMInput(dados.tempo_do_intervalo)}
          nomeAutorizador={dados.aceite_jornada_irregular || ""}
          onChangeNome={(nome) =>
            setDados((p) => ({
              ...p,
              aceite_jornada_irregular: nome,
            }))
          }
          onConfirm={() => {
            setDados((p) => ({
              ...p,
              quantidade_horas_irregulares:
                excessoMinutos > 0
                  ? toHHMMSSFromMinutes(excessoMinutos)
                  : "",
            }));
            setJornadaModalAberto(false);
          }}
        />
      )}
    </div>
  );
}
